import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle, ShieldCheck, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

const MAX_POLLS = 6;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [account, setAccount] = useState(null);

  const poll = useCallback(async (attempt) => {
    if (!sessionId) { setState("error"); return; }
    if (attempt >= MAX_POLLS) { setState("pending"); return; }
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      if (data.payment_status === "paid") {
        if (data.account) setAccount(data.account);
        setState("paid");
        return;
      }
      if (data.status === "expired") { setState("error"); return; }
      setTimeout(() => poll(attempt + 1), 1500);
    } catch (e) {
      setTimeout(() => poll(attempt + 1), 1500);
    }
  }, [sessionId]);

  useEffect(() => { poll(0); }, [poll]);

  const ICON = { checking: Loader2, paid: CheckCircle2, pending: Loader2, error: XCircle };
  const Icon = ICON[state];

  const copy = {
    checking: { title: "Confirming your subscription…", body: "Hang tight while we verify your payment with Stripe." },
    paid: { title: "You're subscribed to Vaultvec", body: "Your subscription is active. Sign in to access your protection console." },
    pending: { title: "Payment is processing", body: "This is taking a little longer than usual. Your confirmation will arrive by email shortly." },
    error: { title: "We couldn't confirm this payment", body: "If you were charged, contact info@vaultvec.com and we'll sort it out right away." },
  }[state];

  return (
    <div className="min-h-screen bg-background text-foreground grid-texture flex items-center justify-center p-6">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md border border-border bg-background p-10 text-center" data-testid="payment-success-card">
        <Link to="/" className="inline-flex items-center gap-2 mb-8">
          <div className="h-7 w-7 bg-primary flex items-center justify-center"><ShieldCheck className="h-4 w-4 text-white" /></div>
          <span className="font-heading text-lg font-extrabold tracking-tighter">VAULTVEC</span>
        </Link>
        <div className="flex justify-center mb-6">
          <Icon className={`h-12 w-12 ${state === "paid" ? "text-[hsl(var(--chart-2))]" : state === "error" ? "text-primary" : "text-primary animate-spin"}`} />
        </div>
        <h1 className="font-heading text-2xl font-bold tracking-tight" data-testid="payment-status">{copy.title}</h1>
        <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{copy.body}</p>
        {state === "paid" && account?.created && account?.temp_password && (
          <div className="mt-6 border border-[hsl(var(--chart-2))]/40 bg-[hsl(var(--chart-2))]/10 p-4 text-left" data-testid="account-credentials">
            <p className="font-mono text-[10px] tracking-widest text-[hsl(var(--chart-2))] mb-2">YOUR NEW CONSOLE ACCOUNT</p>
            <p className="font-mono text-xs text-foreground">Email: <span className="text-muted-foreground">{account.email}</span></p>
            <p className="font-mono text-xs text-foreground mt-1">Temp password: <span className="text-muted-foreground">{account.temp_password}</span></p>
            <p className="text-[11px] text-muted-foreground mt-2">Save these now — change your password after signing in. A copy was also emailed to you.</p>
          </div>
        )}
        {state === "paid" && account && !account.created && (
          <p className="mt-4 text-xs text-muted-foreground">Your subscription is linked to <span className="font-mono">{account.email}</span>. Sign in with your existing password.</p>
        )}
        <div className="mt-8 flex flex-col gap-3">
          {state === "paid" && (
            <Link to="/login">
              <Button data-testid="payment-console-btn" className="w-full rounded-none bg-primary hover:bg-primary/90 text-white h-11">Access Console <ArrowUpRight className="ml-2 h-4 w-4" /></Button>
            </Link>
          )}
          <Link to="/">
            <Button variant="outline" className="w-full rounded-none border-border h-11">Back to home</Button>
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
